import { Box } from '@chakra-ui/layout'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  Text as ChakraText,
} from '@chakra-ui/react'
import type { AnalyticsAPIResponse, BandwidthGraphData } from '../../interfaces'
import { UserContext } from '../../providers'
import Graph, { ExtensionUnitsGraph, StorageGraph, VPUGraph } from './Graph'
import Stats from './Stats'

interface Props {
  dateRange?: {
    startDate: Date
    endDate: Date
  }
  data: AnalyticsAPIResponse
  bandwidthGraphData: BandwidthGraphData | null
  setBandwidthGraphData: Function
}

const Usage = ({
  dateRange,
  data,
  bandwidthGraphData,
  setBandwidthGraphData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const isUserOnImagekitCDN = user?.clientIsOnImageKitCDN

  const [tabIndex, setTabIndex] = useState(0)

  const handleTabsChange = (index): void => {
    setTabIndex(index)
  }

  let dataAvailable = false

  if (
    data.request &&
    data.bandwidth &&
    Object.keys(data.request).length &&
    Object.keys(data.bandwidth).length &&
    data.request.dateWise &&
    data.bandwidth.dateWise &&
    Object.keys(data.request.dateWise).length &&
    Object.keys(data.bandwidth.dateWise).length
  ) {
    dataAvailable = true
  }

  return (
    <Box marginTop="2.5rem">
      <ChakraText fontWeight="semibold" marginBottom="0.75rem" fontSize="16px">
        {t('usage_trend', 'Usage trend')}
      </ChakraText>
      <Divider />
      <Tabs
        marginTop="1rem"
        variant="soft-rounded"
        size="sm"
        colorScheme="appBlue"
        index={tabIndex}
        onChange={handleTabsChange}
      >
        <TabList>
          <Tab>{t('bandwidth_title_case', 'Bandwidth')}</Tab>
          <Tab>{t('requests_title_case', 'Requests')}</Tab>
          <Tab>{t('vpu_title_case', 'VPU')}</Tab>
          <Tab>{t('storage_title_case', 'Storage')}</Tab>
          <Tab>{t('extension', 'Extensions')}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel marginTop="1rem" padding="0">
            {!dataAvailable && (
              <Box textAlign="left">
                {t('no_data_available', 'No Data Available')}
              </Box>
            )}
            {dataAvailable && (
              <React.Fragment>
                <Graph
                  type="bandwidth"
                  {...{
                    data,
                    bandwidthGraphData,
                    setBandwidthGraphData,
                  }}
                />
                {isUserOnImagekitCDN && (
                  <Box>
                    <ChakraText
                      marginTop="2.5rem"
                      fontWeight="semibold"
                      marginBottom="0.75rem"
                      fontSize="16px"
                    >
                      {t(
                        'url-wise-bandwidth-distribution',
                        'URL-Wise Bandwidth Distribution',
                      )}
                    </ChakraText>
                    <Divider />
                    <Stats type="bandwidth" {...{ data }} />
                  </Box>
                )}
              </React.Fragment>
            )}
          </TabPanel>
          <TabPanel marginTop="1rem" padding="0">
            {!dataAvailable && (
              <Box textAlign="left">
                {t('no_data_available', 'No Data Available')}
              </Box>
            )}
            {dataAvailable && (
              <React.Fragment>
                <Graph
                  type="request"
                  {...{
                    data,
                    bandwidthGraphData,
                    setBandwidthGraphData,
                  }}
                />
                {isUserOnImagekitCDN && (
                  <Box>
                    <ChakraText
                      marginTop="2.5rem"
                      fontWeight="semibold"
                      marginBottom="0.75rem"
                      fontSize="16px"
                    >
                      {t(
                        'url-wise-request-distribution',
                        'URL-Wise Request Distribution',
                      )}
                    </ChakraText>
                    <Divider />
                    <Stats type="request" {...{ data }} />
                  </Box>
                )}
              </React.Fragment>
            )}
          </TabPanel>
          <TabPanel marginTop="1rem" padding="0">
            {!dataAvailable && (
              <Box textAlign="left">
                {t('no_data_available', 'No Data Available')}
              </Box>
            )}
            {dataAvailable && (
              <VPUGraph
                vpuUsageData={data.videoProcessingUnits?.dateWise}
                dateRange={dateRange}
              />
            )}
          </TabPanel>
          <TabPanel marginTop="1rem" padding="0">
            {!dataAvailable && (
              <Box textAlign="left">
                {t('no_data_available', 'No Data Available')}
              </Box>
            )}
            {dataAvailable && (
              <StorageGraph
                storageData={data.storage?.dateWise}
                dateRange={dateRange}
              />
            )}
          </TabPanel>
          <TabPanel marginTop="1rem" padding="0">
            {!dataAvailable && (
              <Box textAlign="left">
                {t('no_data_available', 'No Data Available')}
              </Box>
            )}
            {dataAvailable && (
              <ExtensionUnitsGraph
                extensionUsageData={data.extensionUnits?.dateWise}
                dateRange={dateRange}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  )
}

export default Usage
